/**
 * Iter-52: shared question presets for the QuizForm.
 *
 * SOURCE OF TRUTH is the existing standard LandingForm.jsx schema — we
 * only re-arrange the SAME fields into one-question-at-a-time screens.
 * Do NOT add new normalized fields here; the backend intake pipeline
 * would reject them.
 *
 * A step returns an update to the shared form object. All fields listed
 * here already exist in LandingPageLeadIn on the backend.
 */

// Basic step shape:
// {
//   key: "incident_date",           // unique within the quiz
//   title: "When did the accident happen?",
//   subtitle: "It helps us find the right attorney fast.",
//   type: "yesno" | "date" | "select" | "textarea" | "contact" | "consent" | "evidence" | "case_type",
//   field: "physically_injured",    // (yesno/date/textarea/select) → form key
//   options?: [{value, label}]      // for select / case_type
//   required?: boolean
// }

const CORE_QUALIFYING_STEPS = [
  {
    key: "incident_age_bucket",
    title: "When did the accident happen?",
    subtitle: "Pick the window that best matches — no exact date needed.",
    // Iter-62.3: age_bucket replaces the calendar-picker `date` step
    // for the consumer-facing incident-timing question. The exact-date
    // `incident_date` field remains OPTIONAL in the payload; the
    // backend `_check_incident_date` gate soft-accepts null so routing
    // stays compatible.
    type: "age_bucket",
    field: "incident_age_bucket",
    required: true,
    options: [
      { value: "within_30_days",    label: "Within 30 days" },
      { value: "31_to_60_days",     label: "Within 60 days" },
      { value: "61_to_90_days",     label: "Within 90 days" },
      { value: "91_days_to_1_year", label: "Within 1 year" },
      { value: "over_1_year",       label: "Over 1 year" },
    ],
  },
  {
    key: "physically_injured",
    title: "Were you physically injured?",
    subtitle: "Even minor injuries can matter for your case.",
    type: "yesno",
    field: "physically_injured",
    required: true,
  },
  {
    key: "medical_treatment",
    title: "Have you received medical treatment?",
    subtitle: "Any ER visit, urgent care, or follow-up counts.",
    type: "yesno",
    field: "medical_treatment",
    required: true,
  },
  {
    key: "at_fault",
    title: "Were you at fault?",
    subtitle: "Be honest — our network handles both sides.",
    type: "yesno",
    field: "at_fault",
    required: true,
  },
  {
    // Iter-63.1.12A — canonical MVA structured field. Applies only to
    // MVA case types (auto / motorcycle / truck / rideshare / etc.).
    // For non-MVA case types (slip_fall / medical_malpractice / etc.)
    // the QuizForm effectiveSteps filter removes this step at runtime.
    key: "at_fault_party_insured",
    title: "Was the other driver or at-fault party insured?",
    subtitle: null,
    type: "tristate",
    field: "at_fault_party_insured",
    required: true,
    onlyIfMvaCaseType: true,
    options: [
      { value: "yes",     label: "Yes" },
      { value: "no",      label: "No" },
      { value: "unknown", label: "I'm Not Sure" },
    ],
  },
  {
    key: "currently_represented",
    title: "Do you already have an attorney for this case?",
    subtitle: "If so, we won't waste your time.",
    type: "yesno",
    field: "currently_represented",
    required: true,
  },
];

const OPTIONAL_DESCRIPTION_STEP = {
  key: "accident_description",
  title: "Anything else that would help the attorney?",
  subtitle: "Optional — you can skip this.",
  type: "textarea",
  field: "accident_description",
  required: false,
  skippable: true,
};

const OPTIONAL_EVIDENCE_STEP = {
  key: "evidence",
  title: "Do you have any evidence you'd like to share?",
  subtitle: "Photos, police report — completely optional.",
  type: "evidence",
  required: false,
  skippable: true,
};

const CONTACT_STEP = {
  key: "contact",
  title: "Where should the attorney reach you?",
  subtitle: "We only share this with the licensed attorney reviewing your case.",
  type: "contact",
  required: true,
};

const CONSENT_STEP = {
  key: "consent",
  title: "One last thing — your consent to be contacted.",
  subtitle: "Required by the TCPA before we can call or text you.",
  type: "consent",
  required: true,
};

/** Case-type step (used only by personal_injury / free_case_review). */
const CASE_TYPE_STEP = {
  key: "case_type",
  title: "What type of case is this?",
  subtitle: "Pick the option that best matches your situation.",
  type: "select",
  field: "case_type",
  required: true,
  options: [
    { value: "auto", label: "Auto accident" },
    { value: "truck", label: "Truck accident" },
    { value: "motorcycle", label: "Motorcycle accident" },
    { value: "rideshare", label: "Rideshare (Uber/Lyft)" },
    { value: "slip_fall", label: "Slip & fall" },
    { value: "medical_malpractice", label: "Medical malpractice" },
    { value: "product_liability", label: "Product liability" },
    { value: "nursing_home", label: "Nursing home neglect / abuse" },
    { value: "wrongful_death", label: "Wrongful death" },
    { value: "dog_bite", label: "Dog bite" },
    { value: "workers_comp", label: "Workers' comp" },
    { value: "other", label: "Something else" },
  ],
};

/** Given a vertical + editor config, produce the ordered step list. */
export const CASE_TYPE_OPTIONS = CASE_TYPE_STEP.options;
export const AGE_BUCKET_OPTIONS = CORE_QUALIFYING_STEPS[0].options;

export function stepsForVertical(vertical, quizConfig = {}) {
  const v = (vertical || "other").toLowerCase();
  const optionalDescriptionEnabled = quizConfig.optional_description_enabled !== false;
  const optionalEvidenceEnabled = quizConfig.optional_evidence_enabled !== false;
  const disableContact = quizConfig.disable_contact_step === true;
  const disableConsent = quizConfig.disable_consent_step === true;

  const steps = [];
  // PI / free-case-review lead with the case-type question.
  if (v === "other") {
    steps.push({ ...CASE_TYPE_STEP });
  }
  steps.push(...CORE_QUALIFYING_STEPS);
  if (optionalDescriptionEnabled) {
    steps.push({ ...OPTIONAL_DESCRIPTION_STEP });
  }
  if (optionalEvidenceEnabled) {
    steps.push({ ...OPTIONAL_EVIDENCE_STEP });
  }
  if (!disableContact) {
    steps.push({ ...CONTACT_STEP });
  }
  if (!disableConsent) {
    steps.push({ ...CONSENT_STEP });
  }
  return steps;
}

export const QUIZ_DEFAULTS = {
  intro_headline: "Let's see if you may qualify",
  intro_subheadline: "A few quick questions. About one minute.",
  contact_headline: "Where should we send your free case review?",
  optional_description_enabled: true,
  optional_evidence_enabled: true,
};
